import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import MediaGallery from '../components/MediaGallery';
import LoadingSpinner from '../components/LoadingSpinner';
import { getProject } from '../api/projects';
import { useTelegram } from '../hooks/useTelegram';
import { Project } from '../types';

const eo: [number, number, number, number] = [0, 0, 0.25, 1];

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showBackButton, openTelegramLink, hapticImpact, hapticSelection } = useTelegram();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'features'>('overview');

  useEffect(() => {
    const cleanup = showBackButton(() => navigate(-1));
    return cleanup;
  }, [showBackButton, navigate]);

  useEffect(() => {
    const fetchProject = async () => {
      const numId = Number(id);
      if (!id || isNaN(numId)) {
        setError('Некорректный ID проекта');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = await getProject(numId);
        setProject(data);
      } catch (err) {
        setError('Не удалось загрузить проект');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  const parseFeatures = (featuresStr: string | null): string[] => {
    if (!featuresStr) return [];
    try {
      const parsed = JSON.parse(featuresStr);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'transparent' }}>
        <Header showBack />
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{ minHeight: '100vh', background: 'transparent' }}>
        <Header showBack />
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#6E6E73' }}>
          <p style={{ fontSize: '14px' }}>{error || 'Проект не найден'}</p>
        </div>
      </div>
    );
  }

  const features = parseFeatures(project.features);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: eo }}
      style={{ minHeight: '100vh', background: 'transparent', color: '#111111' }}
    >
      {/* Header with back */}
      <Header showBack />

      {/* Hero Image with overlay */}
      <MediaGallery
        media={project.media}
        title={project.title}
        category={project.category}
      />

      {/* Segment Control */}
      <div
        style={{
          display: 'flex',
          margin: '20px 24px 0',
          padding: '3px',
          background: '#F6F6F7',
          borderRadius: '10px',
          border: '0.5px solid rgba(0,0,0,0.06)',
        }}
      >
        {(['overview', 'features'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              hapticSelection();
              setActiveTab(tab);
            }}
            style={{
              flex: 1,
              padding: '10px 0',
              border: 'none',
              background: activeTab === tab ? '#FFFFFF' : 'transparent',
              fontFamily: "'Instrument Sans', -apple-system, sans-serif",
              fontSize: '13px',
              fontWeight: 600,
              color: activeTab === tab ? '#111111' : '#AEAEB2',
              borderRadius: '8px',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: 'all 0.25s cubic-bezier(0.25,0.1,0.25,1)',
              boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' : 'none',
            }}
          >
            {tab === 'overview' ? 'Обзор' : 'Что внутри'}
          </button>
        ))}
      </div>

      {/* Tab Content: Overview */}
      {activeTab === 'overview' && (
        <motion.div
          key="overview"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: eo }}
          style={{ padding: '20px 24px 0' }}
        >
          <p style={{ fontSize: '15px', lineHeight: 1.65, color: '#6E6E73', marginBottom: '20px' }}>
            {project.description}
          </p>

          {/* Result block */}
          {project.result_number && project.result_label && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px',
                background: '#F6F6F7',
                borderRadius: '14px',
                border: '0.5px solid rgba(0,0,0,0.06)',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#111111',
                  color: '#FEFEFE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '20px', color: '#111111', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {project.result_number}
                </div>
                <div style={{ fontSize: '12px', color: '#AEAEB2', marginTop: '2px', fontWeight: 500 }}>
                  {project.result_label}
                </div>
              </div>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)', margin: '16px 0 0' }} />
        </motion.div>
      )}

      {/* Tab Content: Features */}
      {activeTab === 'features' && (
        <motion.div
          key="features"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: eo }}
          style={{ padding: '20px 24px 0' }}
        >
          {features.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {features.map((feature, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '14px 0',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: '#111111',
                    borderBottom: i < features.length - 1 ? '0.5px solid rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      marginTop: '1px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#F6F6F7',
                      border: '0.5px solid rgba(0,0,0,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6E6E73',
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ fontSize: '14px', color: '#AEAEB2', textAlign: 'center', padding: '24px 0' }}>
              Подробности скоро появятся
            </p>
          )}
        </motion.div>
      )}

      {/* Bottom spacer */}
      <div style={{ height: project.bot_url ? '120px' : '88px' }} />

      {/* Fixed bottom panel: Try bot + Order CTA */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(245,245,244,0.98) 60%, transparent)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            maxWidth: '480px',
            margin: '0 auto',
            padding: '12px 24px calc(12px + env(safe-area-inset-bottom, 0px))',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {project.bot_url && (
            <motion.button
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.2 }}
              onClick={() => {
                hapticImpact('light');
                openTelegramLink(project.bot_url!);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                border: '1px solid rgba(0,0,0,0.12)',
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: '#111111',
                fontFamily: "'Instrument Sans', -apple-system, sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '-0.01em',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              Посмотреть вживую
            </motion.button>
          )}
          <motion.button
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.3 }}
            onClick={() => {
              hapticImpact('medium');
              navigate('/order');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              border: 'none',
              background: '#1D1D1F',
              color: '#FEFEFE',
              fontFamily: "'Instrument Sans', -apple-system, sans-serif",
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '-0.01em',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Создать похожий проект
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectDetail;
